import Ajv from 'ajv';
import type { JSONSchema7, JSONSchema7Definition } from 'json-schema';

import type * as api from '@gitbook/api-client';

import deterministicSchemaPropsMetaSchema from './meta-schemas/site-adaptive-deterministic-jsonschema-claims-properties.json';
import deterministicSchemaPrimitivesMetaSchema from './meta-schemas/site-adaptive-deterministic-jsonschema-primitives.json';

const SCHEMAS_BASE_URL = 'https://schemas.gitbook.com';
const DETERMINISTIC_PROPS_META_SCHEMA_REF = `${SCHEMAS_BASE_URL}/SiteAdaptiveDeterministicJSONSchemaClaimsProperties.json`;

/**
 * Keywords kept on the objects of a deterministic schema: claim normalization at the edge only
 * understands these, along with `type`, `properties` and `additionalProperties`.
 */
const DETERMINISTIC_OBJECT_KEYWORDS = [
    '$id',
    '$comment',
    'title',
    'description',
    'examples',
] as const;

const deterministicSchemaAjv = new Ajv({ allErrors: true, removeAdditional: 'all' });

/**
 * Return an ajv validator to validate a deterministic properties JSON schema.
 */
function getDeterministicPropertiesSchemaValidator() {
    let validate = deterministicSchemaAjv.getSchema(DETERMINISTIC_PROPS_META_SCHEMA_REF);
    if (!validate) {
        deterministicSchemaAjv.addMetaSchema(deterministicSchemaPrimitivesMetaSchema);
        deterministicSchemaAjv.addMetaSchema(deterministicSchemaPropsMetaSchema);

        validate = deterministicSchemaAjv.getSchema(DETERMINISTIC_PROPS_META_SCHEMA_REF);

        if (!validate) {
            throw new Error(`No schema registered for ${DETERMINISTIC_PROPS_META_SCHEMA_REF}`);
        }
    }
    return validate;
}

/**
 * Extract a deterministic version (i.e with only restricted properties types) of the visitor JSON schema.
 */
export function extractDeterministicJSONSchemaFromVisitorSchema(
    schema: JSONSchema7 & Pick<api.SiteAdaptiveJSONSchemaWithRegions, 'regions'>
): api.SiteAdaptiveJSONSchemaWithRegions {
    const isDeterministic = getDeterministicPropertiesSchemaValidator();

    function filterDeterministicProperties(
        props: Record<string, JSONSchema7Definition>
    ): Record<string, JSONSchema7Definition> {
        const filtered: Record<string, JSONSchema7Definition> = {};

        for (const [key, def] of Object.entries(props)) {
            if (typeof def === 'boolean') {
                continue;
            }

            if (def.type === 'object' && def.properties) {
                const nested = filterDeterministicProperties(def.properties);
                if (Object.keys(nested).length > 0) {
                    filtered[key] = {
                        ...pickDeterministicObjectKeywords(def),
                        type: 'object',
                        properties: nested,
                        additionalProperties: false,
                    };
                }
            } else if (isDeterministic(def)) {
                filtered[key] = def;
            }
        }

        return filtered;
    }

    const deterministicSchema: api.SiteAdaptiveJSONSchemaWithRegions = {
        ...pickDeterministicObjectKeywords(schema),
        ...(schema.regions ? { regions: schema.regions } : {}),
        $schema: 'http://json-schema.org/draft-07/schema#',
        type: 'object',
        properties: schema.properties ? filterDeterministicProperties(schema.properties) : {},
        additionalProperties: false,
    };

    return deterministicSchema;
}

function pickDeterministicObjectKeywords(schema: JSONSchema7): JSONSchema7 {
    const picked: JSONSchema7 = Object.fromEntries(
        DETERMINISTIC_OBJECT_KEYWORDS.flatMap((keyword) =>
            schema[keyword] === undefined ? [] : [[keyword, schema[keyword]]]
        )
    );
    if (
        Array.isArray(schema.required) &&
        schema.required.every((property) => typeof property === 'string')
    ) {
        picked.required = schema.required;
    }
    return picked;
}
